import uniqBy from 'lodash/uniqBy';
import moment from 'moment';

import i18n from 'shared/lib/i18n';

import { isAdmin, isPM, loadUser, toCamelCaseWithSpaces } from '../../utils';
import { getMomentDateAndTime } from '../Amenity/utils';
import { getRoleName } from '../Person/utils';

export const getVisitorFormData = (data) => {
  const { authorizerOptions, buildingOptions, unitOptions, ...formData } =
    data || {};

  const {
    sendElectronicKey,
    period,
    doors,
    allowedTimeStart: momentStartDate,
    allowedTimeEnd: momentEndDate,
    ...visitorData
  } = formData || {};

  let allowedTimeStart = null;
  let allowedTimeEnd = null;
  if (momentStartDate) {
    const [, time] = getMomentDateAndTime(momentStartDate);
    allowedTimeStart = time && time.slice(0, 8);
  }
  if (momentEndDate) {
    const [, time] = getMomentDateAndTime(momentEndDate);
    allowedTimeEnd = time && time.slice(0, 8);
  }

  const inviteData = { period, doors };
  return {
    sendElectronicKey,
    visitorData: { ...visitorData, allowedTimeStart, allowedTimeEnd },
    inviteData,
  };
};

export const getElectronicKeyPeriodOptions = (phoneNumber) => [
  {
    value: '15min',
    label: phoneNumber
      ? i18n.t('visitor.fifteenMinKey')
      : i18n.t('visitor.fifteenMinKeyPhone'),
    isDisabled: !phoneNumber,
  },
  {
    value: 'unlimited',
    label: i18n.t('visitor.multiEntry'),
  },
];

// allowedRoles, forbiddenPersons, available - temporary unavailable, disabled - hidden, isActive - not yet initialize
// to open a door - available && !disabled && isActive
const getAllowedDoors = (doors, roleId, personId) =>
  doors?.length
    ? doors
        .filter(({ disabled }) => !disabled)
        .filter(({ allowedRoles }) => allowedRoles?.includes(+roleId))
        .filter(
          ({ forbiddenPersons }) => !forbiddenPersons?.includes(+personId),
        )
    : [];

const getBuildingDoors = (doors) => {
  if (!doors?.length) return [];
  return doors.filter(({ isUnitDoor }) => !isUnitDoor);
};

const getUnitDoors = (doors, unitId) => {
  if (!doors?.length) return [];
  if (!unitId) return doors;
  return doors.filter(
    ({ property, relatedUnitProperties }) =>
      property === unitId || relatedUnitProperties.includes(unitId),
  );
};

export function getVisitorDoors(doors, selectedUnitId) {
  // keep only doors available for visitors (role id - 6)
  const allowedDoors = getAllowedDoors(doors, 6);
  const buildingDoors = getBuildingDoors(allowedDoors);
  const unitDoors = getUnitDoors(allowedDoors, selectedUnitId);
  return Array.from(new Set([...buildingDoors, ...unitDoors]));
}

export const filterVisitorDoors = (buildingDoors, visitor) => {
  const {
    role,
    person: { id },
    unit,
  } = visitor;
  const allowedDoors = getAllowedDoors(buildingDoors, role, id);
  const visitorBuildingDoors = getBuildingDoors(allowedDoors);
  const visitorUnitDoors = getUnitDoors(allowedDoors, unit);
  return Array.from(new Set([...visitorBuildingDoors, ...visitorUnitDoors]));
  // what about brokers? should the have access to the unit doors or only on the building level?
  // should it be possible to add a visitor to the forbidden persons arr?
};

export const getBrokerDoors = (doors, selectedBuildings) => {
  if (!doors?.length || !selectedBuildings?.length) return [];
  return uniqBy(doors, 'id')
    .filter(
      ({ building }) =>
        !selectedBuildings?.length ||
        selectedBuildings?.map((buildingId) => +buildingId)?.includes(building),
    )
    .map((door) => ({
      ...door,
      name: `${door?.buildingName} - ${door?.name}`,
    }));
};

export function formatDoorName(doors) {
  if (!doors.length) return [];

  const doorsMap = new Map();
  doors.forEach(({ name }) => {
    if (doorsMap.has(name)) {
      doorsMap.set(name, doorsMap.get(name) + 1);
    } else {
      doorsMap.set(name, 1);
    }
  });

  const formattedDoors = doors.map(({ name, disabled, provider, ...rest }) => {
    let label = name;

    if (doorsMap.get(name) > 1) {
      label = `${label} (${toCamelCaseWithSpaces(provider)})`;
    }
    if (disabled) {
      label = `${label} (${i18n.t('doors.disabled')})`;
    }

    return { name: label, disabled, provider, ...rest };
  });

  return formattedDoors;
}

export const getDefaultBuildingDoorsIds = (doors) => {
  if (!doors?.length) return [];
  return [
    ...new Set(
      doors
        .filter(({ defaultInBuilding }) => defaultInBuilding)
        .map(({ id }) => id),
    ),
  ];
};

export const getAuthType = (type) => {
  if (!type) return '';
  let authType;
  switch (type) {
    case 'password':
      authType = i18n.t('auth.type.password');
      break;
    case 'google-oauth2':
      authType = i18n.t('auth.type.google');
      break;
    case 'facebook':
      authType = i18n.t('auth.type.facebook');
      break;
    case 'apple-id':
      authType = i18n.t('auth.type.apple');
      break;
    default:
      authType = '';
      break;
  }
  return authType;
};

export const getAuthInfo = ({ email, phoneNumber, type }) => {
  if ((!email && !phoneNumber) || !type) return null;
  const registeredWith = email
    ? i18n.t('auth.email')
    : phoneNumber
    ? i18n.t('auth.phoneNumber')
    : null;
  if (!registeredWith) return null;
  const authType = getAuthType(type);
  if (!authType) return null;
  return i18n.t('auth.registeredInfo', { registeredWith, authType });
};

export const getStartDayTime = () => moment.utc().startOf('day');
export const getEndDayTime = () => moment.utc().endOf('day');
export const isSameTime = (startTime, endTime) => {
  const isSameStartTime = moment.utc(startTime).isSame(getStartDayTime());
  const isSameEndTime = moment.utc(endTime).isSame(getEndDayTime());
  return Boolean(isSameStartTime && isSameEndTime);
};

export const getPreselectedInitialValues = ({ location = {} }) => {
  const { state } = location;
  const building = state?.building || state?.buildingId;
  const buildingName = state?.buildingName;
  const unit = state?.unit;
  const unitName = state?.unitName;
  const authorizer = state?.id;
  const authorizerName = state?.authorizerName;

  const initialValues = {
    allowedTimeStart: getStartDayTime(),
    allowedTimeEnd: getEndDayTime(),
  };

  if (building && buildingName) {
    initialValues.building = building;
    initialValues.buildingOptions = [{ label: buildingName, value: building }];
  }
  if (unit && unitName) {
    initialValues.unit = unit;
    initialValues.unitOptions = [{ label: unitName, value: unit }];
  }
  if (authorizer && authorizerName) {
    initialValues.authorizer = authorizer;
    initialValues.authorizerOptions = [
      {
        label: authorizerName,
        value: authorizer,
      },
    ];
  }

  return initialValues;
};

export const getPersonOptions = (selectedUnit) => {
  const residents = selectedUnit?.residents ?? [];
  return residents.map((resident) => {
    const roleName = getRoleName(resident?.role);
    return {
      label: `${resident?.firstName} ${resident?.lastName} (${roleName})`,
      value: resident?.id,
    };
  });
};

export const getIsCUCreatorOrAuthorizer = (visitor) => {
  // visitor or broker
  const cuId = loadUser()?.id;
  if (!cuId) return false;
  const authorizerId = visitor?.authorizer?.id;
  const creatorId = visitor?.creator?.id;
  return cuId === authorizerId || cuId === creatorId;
};

export const canEditVisitor = (visitor) =>
  getIsCUCreatorOrAuthorizer(visitor) || isAdmin();

export const canEditBroker = (params) => isPM(params) || isAdmin();

export const getPropertyInfo = ({ pmc, pmcName, building, buildingName }) => {
  const propertyId = building || pmc;
  const propertyName = buildingName || pmcName;
  const type = building ? 'building' : pmc ? 'pmc' : null;
  return { id: propertyId, name: propertyName, type };
};

export const getVisitorRoleFilterOptions = () => [
  {
    value: '6',
    label: i18n.t('title.visitor'),
  },
  {
    value: '9',
    label: i18n.t('roles.broker'),
  },
  {
    value: '10',
    label: i18n.t('visitor.prospectiveTenant'),
  },
];
